import React, { useEffect, useState } from 'react'
import { Box, CircularProgress, Grid, Stack, TextField, Typography, useMediaQuery, useTheme, Snackbar, Alert } from '@mui/material'
import { Navbar } from '../features/navigation/components/Navbar'
import { Footer } from '../features/footer/Footer'
import { DiscountCard } from '../components/DiscountCard'
import { axiosi } from '../config/axios'

export const DiscountOffersPage = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [discounts, setDiscounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })

  useEffect(() => {
    fetchDiscounts()
  }, [])

  const fetchDiscounts = async () => {
    try {
      setLoading(true)
      const response = await axiosi.get('/coupons')
      // Filter only active, non-expired coupons
      const activeCoupons = response.data.filter(coupon => {
        const isActive = coupon.isActive
        const isNotExpired = !coupon.expiresAt || new Date(coupon.expiresAt) > new Date()
        return isActive && isNotExpired
      })
      setDiscounts(activeCoupons)
    } catch (error) {
      console.error('Error fetching discounts:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code)
    setSnackbar({
      open: true,
      message: `Code "${code}" copied to clipboard!`
    })
  }

  const filteredDiscounts = discounts.filter(discount =>
    discount.code.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <>
      <Navbar />
      
      <Box sx={{ minHeight: '80vh', py: 6, px: isMobile ? 2 : 4, backgroundColor: '#f9f9f9' }}>
        <Stack spacing={4}>
          {/* Header */}
          <Stack spacing={2} textAlign="center">
            <Typography 
              variant="h2" 
              sx={{
                fontWeight: 900,
                fontSize: isMobile ? '2.15rem' : '3.25rem',
                color: 'primary.main',
                lineHeight: 1.1
              }}
            >
              🎉 Special Offers & Discounts
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Check out our amazing discounts and save on your favorite products
            </Typography>
          </Stack>

          {/* Search Bar */}
          <Box sx={{ maxWidth: 500, mx: 'auto', width: '100%' }}>
            <TextField
              fullWidth
              placeholder="Search discount codes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Box>

          {/* Loading */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredDiscounts.length > 0 ? (
            <>
              {/* Discounts Grid */}
              <Grid container spacing={3}>
                {filteredDiscounts.map((discount) => (
                  <Grid item xs={12} sm={6} md={4} key={discount._id}>
                    <DiscountCard 
                      coupon={discount}
                      onCopy={handleCopy}
                    />
                  </Grid>
                ))}
              </Grid>

              <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mt: 2 }}>
                Showing {filteredDiscounts.length} active offer{filteredDiscounts.length !== 1 ? 's' : ''}
              </Typography>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="textSecondary">
                No discounts found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {searchTerm ? 'Try a different search term' : 'Check back later for new offers!'}
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>

      <Snackbar 
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity="success" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </>
  )
}
